import { AppEvent, emitter } from '@/plugin/mitt';
import { store } from '@/store';
import { globalReset } from '@/store/actions';
import { setMustChangePassword } from '@/store/slices/authSlice';
import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import tokenRefreshService from './tokenRefreshService';

// API Base Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

// Create axios instance
const api: AxiosInstance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 300000,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('access');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    },
);

// Response interceptor to handle token refresh and clear Redux state on 401
api.interceptors.response.use(
    (response: AxiosResponse) => {
        return response;
    },
    async (error: AxiosError) => {
        const errorData = error.response?.data as { code?: string } | undefined;

        if (error.response?.status === 403 && errorData?.code === 'ERROR_MUST_CHANGE_PASSWORD') {
            store.dispatch(setMustChangePassword(true));
            if (window.location.pathname !== '/change-password-required') {
                emitter.emit(AppEvent.NAVIGATE, { to: '/change-password-required' });
            }
            return Promise.reject(error);
        }

        if (error.response?.status === 401) {
            try {
                // Try to refresh the token
                const response = await tokenRefreshService.handleTokenRefresh(error);
                return response;
            } catch (refreshError) {
                // If refresh fails, clear all Redux state using global reset
                store.dispatch(globalReset());

                // Handle unauthorized access
                localStorage.removeItem('access');
                localStorage.removeItem('refresh');

                // Redirect to login
                if (window.location.pathname !== '/auth/login') {
                    emitter.emit(AppEvent.NAVIGATE, { to: '/auth/login' });
                }
            }
        }
        return Promise.reject(error);
    },
);

export default api;
